import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { BaseInput, CodeEntrySlots, PrimaryButton } from '../../../shared/ui';

type MyPageSettingsPinScreenProps = {
  autoFocus?: boolean;
  hasPinProtection: boolean;
  onPhaseChange?: (phase: MyPageSettingsPinPhase) => void;
  onValidateCurrentPin?: (pin: string) => Promise<boolean>;
  onSubmit: (payload: {
    currentPin: string | null;
    nextPin: string;
  }) => Promise<boolean>;
  onComplete?: () => void;
  showPrompt?: boolean;
};

export type MyPageSettingsPinPhase = 'current' | 'new' | 'confirm';

const PIN_LENGTH = 4;

function sanitizePin(value: string): string {
  return value.replace(/\D/g, '').slice(0, PIN_LENGTH);
}

const pinFieldErrorShake = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }

  20% {
    transform: translateX(-6px);
  }

  40% {
    transform: translateX(6px);
  }

  60% {
    transform: translateX(-4px);
  }

  80% {
    transform: translateX(4px);
  }
`;

const Screen = styled.section.attrs({
  'data-ui': 'my-page-settings-pin-screen',
})`
  display: grid;
  width: 100%;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(35)};
`;

const EntrySection = styled.div`
  width: 100%;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(22)};
`;

const FieldFeedback = styled.div`
  width: ${({ theme }) => theme.scale.px(331)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(6)};
  justify-self: center;
`;

const Prompt = styled.p`
  margin: 0;
  text-align: left;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.25;
`;

const PinField = styled.div.attrs({
  'data-ui': 'my-page-settings-pin-field',
})<{ $hasError: boolean }>`
  position: relative;
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(57)};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surface.subtle};
  border: ${({ $hasError, theme }) =>
    $hasError
      ? `${theme.scale.px(2)} solid ${theme.colors.support.danger}`
      : `${theme.scale.px(1)} solid ${theme.colors.border.subtle}`};
  transition: background-color 160ms ease, box-shadow 160ms ease;
  cursor: text;
  animation: ${({ $hasError }) => ($hasError ? pinFieldErrorShake : 'none')}
    320ms ease;

  &:focus-within {
    background: ${({ theme }) => theme.colors.surface.overlay};
    box-shadow: ${({ $hasError, theme }) =>
      $hasError
        ? 'none'
        : `0 0 0 ${theme.scale.px(2)} ${theme.colors.brand.primary}`};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HiddenInput = styled(BaseInput)`
  position: absolute;
  width: ${({ theme }) => theme.scale.px(1)};
  height: ${({ theme }) => theme.scale.px(1)};
  padding: 0;
  margin: ${({ theme }) => theme.scale.px(-1)};
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const ContinueButton = styled(PrimaryButton)<{ $isDisabled: boolean }>`
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  justify-self: center;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.support.danger};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

function resolvePinPrompt(
  phase: MyPageSettingsPinPhase,
  t: (key: string) => string,
): string {
  if (phase === 'current') {
    return t('myPageSettings.pin.currentLabel');
  }

  return phase === 'new'
    ? t('myPageSettings.pin.newLabel')
    : t('myPageSettings.pin.confirmLabel');
}

export function MyPageSettingsPinScreen({
  autoFocus = false,
  hasPinProtection,
  onPhaseChange,
  onValidateCurrentPin,
  onSubmit,
  onComplete,
  showPrompt = true,
}: MyPageSettingsPinScreenProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [phase, setPhase] = useState<MyPageSettingsPinPhase>(
    hasPinProtection ? 'current' : 'new',
  );
  const [currentPin, setCurrentPin] = useState<string | null>(null);
  const [draftPin, setDraftPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReadyToContinue = draftPin.length === PIN_LENGTH;

  const updatePhase = (nextPhase: MyPageSettingsPinPhase) => {
    setPhase(nextPhase);
    onPhaseChange?.(nextPhase);
  };

  const focusPinInput = () => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handlePinChange = (value: string) => {
    setErrorMessage(null);
    setDraftPin(sanitizePin(value));
  };

  const handleContinue = async () => {
    if (!isReadyToContinue) {
      return;
    }

    if (phase === 'current') {
      setIsSubmitting(true);

      try {
        const isCurrentPinValid = onValidateCurrentPin
          ? await onValidateCurrentPin(draftPin)
          : true;

        if (!isCurrentPinValid) {
          setDraftPin('');
          setErrorMessage(t('myPageSettings.pin.invalidCurrent'));
          focusPinInput();
          return;
        }

        setCurrentPin(draftPin);
        setDraftPin('');
        updatePhase('new');
        focusPinInput();
      } catch {
        setErrorMessage(t('myPageSettings.pin.unavailable'));
        focusPinInput();
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    if (phase === 'new') {
      setNewPin(draftPin);
      setDraftPin('');
      updatePhase('confirm');
      focusPinInput();
      return;
    }

    if (draftPin !== newPin) {
      setDraftPin('');
      setErrorMessage(t('myPageSettings.pin.mismatch'));
      focusPinInput();
      return;
    }

    setIsSubmitting(true);

    try {
      const didSubmit = await onSubmit({
        currentPin,
        nextPin: draftPin,
      });

      if (!didSubmit) {
        setCurrentPin(null);
        setDraftPin('');
        setNewPin('');
        setErrorMessage(t('myPageSettings.pin.invalidCurrent'));
        updatePhase(hasPinProtection ? 'current' : 'new');
        focusPinInput();
        return;
      }

      onComplete?.();
    } catch {
      setErrorMessage(t('myPageSettings.pin.unavailable'));
      focusPinInput();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Screen>
      <EntrySection>
        {showPrompt ? <Prompt>{resolvePinPrompt(phase, t)}</Prompt> : null}

        <FieldFeedback>
          <PinField
            $hasError={errorMessage !== null}
            onClick={() => {
              inputRef.current?.focus();
            }}
          >
            <HiddenInput
              ref={inputRef}
              aria-label={resolvePinPrompt(phase, t)}
              autoFocus={autoFocus}
              autoComplete="one-time-code"
              inputMode="numeric"
              value={draftPin}
              onChange={event => {
                handlePinChange(event.target.value);
              }}
            />

            <CodeEntrySlots
              code={draftPin}
              codeLength={PIN_LENGTH}
              digitFontSizePx={20}
            />
          </PinField>

          {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
        </FieldFeedback>
      </EntrySection>

      <ContinueButton
        $isDisabled={!isReadyToContinue || isSubmitting}
        type="button"
        disabled={!isReadyToContinue || isSubmitting}
        onClick={() => {
          void handleContinue();
        }}
      >
        {t('myPageSettings.pin.continue')}
      </ContinueButton>
    </Screen>
  );
}
